/**
 * Supabase-backed persistence (Phase 5b, step 5).
 *
 * Implements the same `ScenarioRepository` interface as
 * `LocalStorageRepository`, so swapping backends is a composition-root
 * decision — no editor call site changes. The draft/published JSON stored
 * here is byte-identical to what Export produces and what localStorage holds;
 * the format crosses every boundary unchanged.
 *
 * VERIFICATION STATUS: this builds and typechecks against the real
 * @supabase/supabase-js types, and the default (localStorage) path is
 * unaffected. The live network round-trip — auth, RLS-filtered reads, upserts
 * — has NOT been exercised in the build sandbox (no network egress to
 * *.supabase.co). Confirm it by running the editor with env vars set against
 * the real project. The likely first-run snags are documented inline.
 *
 * Table (created in step 2):
 *   public.scenarios(id text pk, owner uuid -> auth.users, draft jsonb,
 *                    published_versions jsonb, created_at, updated_at)
 *   RLS: every operation gated on `owner = auth.uid()`.
 *
 * Identity: the editor assigns ids client-side (`scn_...`), matching the
 * ScenarioRepository contract. That's why `id` is `text`, not a DB-generated
 * uuid.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  ScenarioRepository,
  ScenarioSummary,
  StoredScenario,
} from './ScenarioRepository';

/** Shape of a row in the `scenarios` table (snake_case, as Postgres returns). */
interface ScenarioRow {
  id: string;
  owner: string;
  draft: StoredScenario['draft'];
  published_versions: StoredScenario['publishedVersions'];
  created_at: string;
  updated_at: string;
}

export class SupabaseRepository implements ScenarioRepository {
  constructor(private readonly client: SupabaseClient) {}

  /** The current user's id, or throw — every write is owner-scoped. */
  private async requireUserId(): Promise<string> {
    const {
      data: { user },
      error,
    } = await this.client.auth.getUser();
    if (error) throw error;
    if (!user) {
      throw new Error('Not signed in — cannot access scenarios.');
    }
    return user.id;
  }

  async list(): Promise<ScenarioSummary[]> {
    // RLS restricts this to the signed-in user's rows automatically; no
    // explicit owner filter needed. (If you're signed out, this returns [].)
    const { data, error } = await this.client
      .from('scenarios')
      .select('id, draft, published_versions, updated_at')
      .order('updated_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map((row) => {
      const r = row as Pick<ScenarioRow, 'id' | 'draft' | 'published_versions' | 'updated_at'>;
      return {
        id: r.id,
        title: r.draft?.title ?? 'Untitled scenario',
        updatedAt: r.updated_at,
        publishedCount: (r.published_versions ?? []).length,
      };
    });
  }

  async load(id: string): Promise<StoredScenario | null> {
    const { data, error } = await this.client
      .from('scenarios')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    const row = data as ScenarioRow;
    return {
      id: row.id,
      draft: row.draft,
      publishedVersions: row.published_versions ?? [],
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }

  async save(record: StoredScenario): Promise<void> {
    const owner = await this.requireUserId();
    // Upsert on the primary key. `owner` must be the signed-in user or the
    // RLS `with check (owner = auth.uid())` policy rejects the write.
    const { error } = await this.client.from('scenarios').upsert(
      {
        id: record.id,
        owner,
        draft: record.draft,
        published_versions: record.publishedVersions,
        created_at: record.createdAt,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'id' },
    );
    if (error) throw error;
  }

  async remove(id: string): Promise<void> {
    // RLS ensures a user can only delete their own rows; an id they don't own
    // simply matches nothing.
    const { error } = await this.client.from('scenarios').delete().eq('id', id);
    if (error) throw error;
  }
}
